// One shared author-identity renderer for messages (#507).
//
// Before this, ChatMessage inlined the author identity (isAgent / displayName /
// the webhook-card avatar split) and MessageList / ThreadPanel each shaped their
// typing-indicator authors differently ({fromName?} vs {fromId, fromName}). The
// rules were the same everywhere; the code wasn't. This resolves the parts of an
// author's identity that live ON the message itself — no reactive app state — so
// the row header, the typing indicator, and <AuthorAvatar> all agree.
//
// What's intrinsic to a message (pure, resolvable here):
//   - userId : message.fromId
//   - name   : message.fromName, else the fromId prefix (the header fallback)
//   - type   : message.fromType ("human" | "agent" | "system")
//   - image  : ONLY the webhook-card author avatar (or the Cyborg app icon for
//              Cyborg's own alerts, isCyborgSystemPost). A webhook post must never
//              show the token owner's face, so its avatar is the release/event
//              card author's own GitHub avatar — and that travels on the message.
//
// What is NOT resolved here (it needs reactive state, so it stays in
// <AuthorAvatar>): a human's member image (getMemberImage), a cybo's roster
// avatar/provider glyph (cyboState / workspaceState.agents). Keeping those out
// is what makes this a pure, unit-testable function.

import type { Message } from "$lib/types.js";

export type MessageAuthorType = "human" | "agent" | "system";

export interface MessageAuthor {
  userId: string;
  name: string;
  type: MessageAuthorType;
  // Message-intrinsic avatar: the webhook card author's image, or null. Reactive
  // images (member photo, cybo avatar) are resolved by <AuthorAvatar>, not here.
  image: string | null;
}

// Display name resolution, identical to ChatMessage's old `displayName`:
// fromName when present, else the first 8 chars of the fromId — unless the
// caller opts in with `idFallback`, which renders instead (default omitted =
// unchanged for every existing caller, e.g. the channel row's id fragment).
export function authorName(
  message: Pick<Message, "fromName" | "fromId">,
  idFallback?: string,
): string {
  return message.fromName ?? idFallback ?? message.fromId.slice(0, 8);
}

// A webhook post made AS an agent (fromType "agent", fromId = the cybo): it is the cybo's
// message, so it takes the cybo's avatar and name; the webhook badge stays.
export function isWebhookAgentPost(message: Pick<Message, "source" | "fromType">): boolean {
  return message.source === "webhook" && message.fromType === "agent";
}

// A human's CURRENT name. `fromName` is a snapshot taken when the message was
// posted, so a rename only reaches old messages through the live member row.
// A webhook post carries fromType "human" but fromId = the webhook's
// CONFIGURER, not the actor (routes/webhooks.ts) — resolve the actor from
// the card instead (guard mirrors intrinsicImage); no card actor → fromName.
// `idFallback` threads through to authorName() — the one place that decides.
export function liveHumanName(
  message: Pick<Message, "fromId" | "fromType" | "fromName" | "source" | "card">,
  members: readonly { userId: string; name: string | null }[] | undefined,
  idFallback?: string,
): string {
  if (message.source === "webhook" && !isWebhookAgentPost(message)) {
    const c = message.card;
    if (c && "author" in c && c.author?.login) return c.author.login;
    return authorName(message, idFallback);
  }
  if (message.fromType === "human") {
    const live = members?.find((m) => m.userId === message.fromId)?.name?.trim();
    if (live) return live;
  }
  return authorName(message, idFallback);
}

// The message-intrinsic author image. Only webhook posts carry one: the
// release/event card author's GitHub avatar (approval cards (#600), page cards
// (CYBORG-309) and task-activity cards have no author). Everything else → null
// (the component resolves the live image).
function intrinsicImage(message: Message): string | null {
  if (isCyborgNotice(message)) return CYBORG_LOGO;
  if (message.source !== "webhook" || isWebhookAgentPost(message)) return null;
  if (isCyborgSystemPost(message)) return CYBORG_LOGO;
  const c = message.card;
  // Structural, not a kind list: only the release/event webhook cards carry an
  // author at all, and a per-kind exclusion chain silently breaks (or, here,
  // fails to compile) every time a new card kind lands.
  if (!c || !("author" in c)) return null;
  return c.author?.avatarUrl ?? null;
}

// Cyborg's own channel alerts (the relay's desktop-update digest) arrive as a plain
// webhook post under this author name. The relay reserves the name on its webhook
// endpoint (server update-failure-alerts.ts SYSTEM_POST_USERNAME — same string), so a
// card-less webhook row carrying it is Cyborg's own and draws the Cyborg app icon.
export const CYBORG_SYSTEM_POST_NAME = "Desktop updates";
export const CYBORG_LOGO = "/icon-192.png";

export function isCyborgSystemPost(
  message: Pick<Message, "source" | "fromName" | "card">,
): boolean {
  return (
    message.source === "webhook" &&
    message.card == null &&
    message.fromName === CYBORG_SYSTEM_POST_NAME
  );
}

export const CYBORG_SYSTEM_AUTHOR_NAME = "Cyborg";

// Cyborg's own unnamed notices (a member could not be reached, a hop limit, a digest). A
// system row posted under an agent's name stays that agent's.
export function isCyborgNotice(message: Pick<Message, "fromType" | "fromName">): boolean {
  return message.fromType === "system" && !message.fromName;
}

export function messageAuthor(message: Message): MessageAuthor {
  return {
    userId: message.fromId,
    name: isCyborgNotice(message) ? CYBORG_SYSTEM_AUTHOR_NAME : authorName(message),
    type: message.fromType,
    image: intrinsicImage(message),
  };
}

// Typing-indicator authors arrive in two shapes — MessageList passes
// `{ fromName? }`, ThreadPanel passes `{ fromId, fromName }` — but both resolve
// the SAME way: the sender's name, or "Someone" when it's unknown (a typing
// event can arrive before the roster has the name). Unify both callers through
// this so the fallback lives in one place.
export function typingAuthorName(author?: { fromName?: string } | null): string {
  return author?.fromName ?? "Someone";
}
